import React, { isValidElement } from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import createLogger from '~/src/Lib/Logging'
import { EMPTY_ARRAY, EMPTY_OBJECT, noop } from '~/src/Lib/Utils'

import { permitted } from './Can'
import { available } from './Has'

export * from './Can'
export { default as Can } from './Can'
export * from './custom'
export * from './Has'
export { default as Has } from './Has'
export * from './utils'

const logger = createLogger('Allowed')

export const allowed = props => {
  const {
    actions = EMPTY_ARRAY,
    anyAction = false,
    features = EMPTY_ARRAY,
    anyFeature = false,
    apps = EMPTY_ARRAY,
    negate = EMPTY_ARRAY,
    tiers = EMPTY_ARRAY,
    // connected props
    myFlags,
    myPermissions,
    config,
  } = props
  let haveFeatures = available(myFlags, features, anyFeature)
  let actionsAllowed = permitted(myPermissions, actions, anyAction)
  let appMatch = !apps?.length
  if (apps?.length) {
    logger.debug('gating by app:', { apps, APP: config.APP })
    appMatch = Array.isArray(apps) ? apps.includes(config.APP) : apps === config.APP
  }
  let tierMatch = !tiers?.length
  if (tiers?.length) {
    logger.debug('gating by tier:', { tiers, TIER: config.TIER })
    tierMatch = Array.isArray(tiers) ? tiers.includes(config.TIER) : tiers === config.TIER
  }
  if (negate.length) {
    logger.debug('negating:', negate)
    if (negate.includes('actions')) {
      actionsAllowed = !actionsAllowed
    }
    if (negate.includes('features')) {
      haveFeatures = !haveFeatures
    }
    if (negate.includes('apps')) {
      appMatch = !appMatch
    }
    if (negate.includes('tiers')) {
      tierMatch = !tierMatch
    }
  }
  logger.debug({ haveFlags: haveFeatures, actionsAllowed, appMatch, tierMatch, ...props })

  return [haveFeatures && actionsAllowed && appMatch && tierMatch, !actionsAllowed]
}

/**
 * Check for allowed actions, features, and apps simultaneously
 * @param {Object} flags
 * @param {string|string[]} flags.actions Actions that the user needs to be permitted
 * @param {boolean} flags.anyAction If more than one action, allow if user is permitted any one of the actions
 * @param {string|string[]} flags.features Features that the user needs to be available
 * @param {boolean} flags.anyFeature If more than one feature, allow if user has at least one.
 * @param {string|string[]} flags.apps Allowed if current selectConfig.APP is present in apps
 * @param {string[]} flags.negate Negates any of the tiers in the tiers list
 * @param {string[]} flags.tiers A tier list that will be negate
 * @returns {[boolean, boolean]} [allowed, actionsNotAllowed]
 */
export const useAllowed = flags => allowed({ ...flags, ...useConnect('selectConfig', 'selectMyFlags', 'selectMyPermissions') })

export const Allowed = ({
  children,
  Fallback: OriginalFallback = noop,
  FallbackProps = EMPTY_OBJECT,
  PermissionFallback = OriginalFallback,
  ...props
}) => {
  const [isAllowed, failedPermissions] = useAllowed(props)
  logger.debug('Allowed:', { isAllowed, failedPermissions, props })
  if (isAllowed) {
    return children
  }
  logger('<<< failedPermissions', failedPermissions)
  const Fallback = failedPermissions ? PermissionFallback : OriginalFallback

  return isValidElement(Fallback) ? Fallback : <Fallback {...FallbackProps} />
}
const flagType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.string,
  PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.string])),
])
const appType = PropTypes.oneOf(['AROYA', 'POMETA'])

/* eslint-disable react/no-unused-prop-types */
Allowed.propTypes = {
  actions: flagType,
  anyAction: PropTypes.bool,
  anyFeature: PropTypes.bool,
  features: flagType,
  apps: PropTypes.oneOfType([appType, PropTypes.arrayOf(appType)]),
  children: PropTypes.node.isRequired,
  Fallback: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  // eslint-disable-next-line react/forbid-prop-types
  FallbackProps: PropTypes.object,
  PermissionFallback: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element])
}
